// src/components/ContactForm.jsx
import { useState, useEffect } from 'react';
import { IMaskInput } from 'react-imask';
import { toast } from 'react-hot-toast';
import { useUser } from '../contexts/UserContext';
import Spinner from './Spinner';

// --- Constants ---
const usStates = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];
const inputClassName = "mt-1 block w-full p-2 border border-neutral rounded focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed text-sm";
const radioClassName = "form-radio text-primary focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed";
const labelClassName = "block text-sm font-medium text-gray-600 mb-1";
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());
const isValidPhone = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};
const hasAnyValidPhone = (data) => ['phone_mobile', 'phone_home', 'phone_work'].some(field => isValidPhone(data?.[field]));
const isConfirmed = (value) => value === true || value === 1 || String(value) === '1';
const registrationVerifiedContacts = (data) => ({
  email: isConfirmed(data?.confirmed_email),
  emailValue: String(data?.email || '').trim().toLowerCase(),
  phone: isConfirmed(data?.confirmed_phone),
  phoneValue: String(data?.phone_mobile || '').replace(/\D/g, ''),
});
const contactAvailability = (data, verifiedContacts = null) => {
  const email = String(data?.email || '').trim().toLowerCase();
  const mobile = String(data?.phone_mobile || '').replace(/\D/g, '');

  return {
    email: isValidEmail(email) && (!verifiedContacts || (
      verifiedContacts.email && email === verifiedContacts.emailValue
    )),
    phone: isValidPhone(mobile) && (!verifiedContacts || (
      verifiedContacts.phone && mobile === verifiedContacts.phoneValue
    )),
  };
};
const availablePreferredContact = (data, requested, verifiedContacts = null) => {
  const availability = contactAvailability(data, verifiedContacts);
  const preferred = String(requested || '').toUpperCase();

  if (preferred === 'EMAIL' && availability.email) return 'EMAIL';
  if (preferred === 'TEXT' && availability.phone) return 'TEXT';
  if (availability.email) return 'EMAIL';
  if (availability.phone) return 'TEXT';
  return '';
};

const applyBillingFromPrimaryAddress = (data) => ({
  ...data,
  billing_address1: data.address1 || '',
  billing_address2: data.address2 || '',
  billing_city: data.city || '',
  billing_state: data.state || '',
  billing_postal_code: data.postal_code || '',
});

function ContactForm({
  initialData = {},
  onSuccess,
  onSaveComplete,
  onSaveSuccess,
  onDirty,
  setLoading: parentSetLoading,
  setError: parentSetError,
  isWorkflowStep = false,
}) {

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const userContext = useUser();
  const {user} = useUser();
  const [isBillingSameAddress, setIsBillingSameAddress] = useState(true);

  const [formData, setFormData] = useState({
    address1: '', address2: '', city: '', state: '', postal_code: '',
    user_name: '',
    phone_home: '', phone_work: '', phone_mobile: '', email: '',
    preferred_phone: '', preferred_contact: '',
  });

  const userid = isWorkflowStep ? initialData?.userid : user?.userid;

  // --- Effect 1: Initialize/Update for WORKFLOW mode ---
  useEffect(() => {
    if (isWorkflowStep && initialData) {
      const data = initialData;
      setIsBillingSameAddress(true);

      // Prepare form data
      const formDataObj = {
        address1: data?.address1 || '',
        address2: data?.address2 || '',
        city: data?.city || '',
        state: data?.state || '',
        postal_code: data?.postal_code || '', // API name
        billing_address1: data?.billing_address1 || '',
        billing_address2: data?.billing_address2 || '',
        billing_city: data?.billing_city || '',
        billing_state: data?.billing_state || '',
        billing_postal_code: data?.billing_postal_code || '', // API name
        phone_home: data?.phone_home || '', // API name
        phone_work: data?.phone_work || '', // API name
        phone_mobile: data?.phone_mobile || '', // API name
        user_name: data?.user_name || initialData?.user_name || data?.email || '',
        email: initialData?.email || data?.email || '', // Email might be top-level
        preferred_contact: data?.preferred_contact?.toUpperCase() || '', // API name & case
        registration_step: "contact",
      };

      // Determine the best preferred phone based on available valid numbers
      const preferredPhone = data?.preferred_phone?.toUpperCase() || 'MOBILE';

      // Check if the preferred phone has a valid number
      const preferredPhoneKey = `phone_${preferredPhone.toLowerCase()}`;
      const hasValidPreferredPhone = isValidPhone(data?.[preferredPhoneKey]);

      if (hasValidPreferredPhone) {
        // Use the existing preferred phone
        formDataObj.preferred_phone = preferredPhone;
      } else {
        // Find the first valid phone number
        const phoneTypes = ['MOBILE', 'HOME', 'WORK'];
        const validPhoneType = phoneTypes.find(type =>
          isValidPhone(data?.[`phone_${type.toLowerCase()}`])
        ) || 'MOBILE'; // Default to MOBILE if none are valid

        formDataObj.preferred_phone = validPhoneType;
      }
      // During registration, only the destination already proved by OTP may
      // be preferred. An alternate contact may be saved for separate proof.
      formDataObj.preferred_contact = availablePreferredContact(
        formDataObj,
        formDataObj.preferred_contact,
        registrationVerifiedContacts(initialData),
      );

      const hasBillingAddress = Boolean(
        formDataObj.billing_address1
        || formDataObj.billing_city
        || formDataObj.billing_state
        || formDataObj.billing_postal_code
      );
      setFormData(hasBillingAddress ? formDataObj : applyBillingFromPrimaryAddress(formDataObj));
      setIsLoading(false);
    }
  }, [isWorkflowStep, initialData]);


  // --- Effect 2: Fetch data for STANDALONE mode ---
  useEffect(() => {
    if (isWorkflowStep) return;
    if (!user?.userid || userContext?.loadingSession) {
        setError("User not identified.");
        setIsLoading(false);
        return;
    }

    setIsLoading(true);
    setError('');

    try {
      const existingData = user;
      if (existingData && typeof existingData === 'object') {
        const addressesMatch =
          (existingData.address1 || '') === (existingData.billing_address1 || '') &&
          (existingData.address2 || '') === (existingData.billing_address2 || '') &&
          (existingData.city || '') === (existingData.billing_city || '') &&
          (existingData.state || '') === (existingData.billing_state || '') &&
          (existingData.postal_code || '') === (existingData.billing_postal_code || '') &&
          (existingData.country || '') === (existingData.billing_country || '');

        // Prepare form data object
        const formDataObj = {
          address1: existingData.address1 || '',
          address2: existingData.address2 || '',
          city: existingData.city || '',
          state: existingData.state || '',
          postal_code: existingData.postal_code || '', // API name
          billing_address1: existingData.billing_address1 || '',
          billing_address2: existingData.billing_address2 || '',
          billing_city: existingData.billing_city || '',
          billing_state: existingData.billing_state || '',
          billing_postal_code: existingData.billing_postal_code || '', // API name
          phone_home: existingData.phone_home || '', // API name
          phone_work: existingData.phone_work || '', // API name
          phone_mobile: existingData.phone_mobile || '', // API name
          email: existingData.email || '',
          user_name: existingData.user_name || existingData.email || '',
          preferred_contact: existingData.preferred_contact?.toUpperCase() || '', // API name & case
        };

        const hasBillingAddress = Boolean(
          formDataObj.billing_address1
          || formDataObj.billing_city
          || formDataObj.billing_state
          || formDataObj.billing_postal_code
        );
        const nextFormData = hasBillingAddress ? formDataObj : applyBillingFromPrimaryAddress(formDataObj);
        setIsBillingSameAddress(!hasBillingAddress || addressesMatch);

        // Determine the best preferred phone based on available valid numbers
        const preferredPhone = existingData.preferred_phone?.toUpperCase() || 'MOBILE';

        // Check if the preferred phone has a valid number
        const preferredPhoneKey = `phone_${preferredPhone.toLowerCase()}`;
        const hasValidPreferredPhone = isValidPhone(existingData[preferredPhoneKey]);

        if (hasValidPreferredPhone) {
          // Use the existing preferred phone
          nextFormData.preferred_phone = preferredPhone;
        } else {
          // Find the first valid phone number
          const phoneTypes = ['MOBILE', 'HOME', 'WORK'];
          const validPhoneType = phoneTypes.find(type =>
            isValidPhone(existingData[`phone_${type.toLowerCase()}`])
          ) || 'MOBILE'; // Default to MOBILE if none are valid

          nextFormData.preferred_phone = validPhoneType;
        }
        nextFormData.preferred_contact = availablePreferredContact(nextFormData, nextFormData.preferred_contact);

        setFormData(nextFormData);
      } else {
          setError(existingData?.error || 'Could not load your contact data.');
      }
    } catch {
        setError('An error occurred while loading your contact data.');
    } finally {
        setIsLoading(false);
    };

    return;
  }, [isWorkflowStep, userid]);


  // --- Input Handlers ---
  const updateFieldValue = (name, value) => {
    setFormData(prev => {
      if (String(prev[name] ?? '') === String(value ?? '')) {
        return prev;
      }

      onDirty?.();
      return { ...prev, [name]: value };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (['email', 'phone_mobile', 'phone_home', 'phone_work'].includes(name)) {
        next.preferred_contact = availablePreferredContact(
          next,
          next.preferred_contact,
          isWorkflowStep ? registrationVerifiedContacts(initialData) : null,
        );
      }
      if (String(prev[name] ?? '') !== String(value ?? '')) onDirty?.();
      return next;
    });
  };

  const handlePreferredChange = (e) => {
    const { name, value } = e.target;

    // If changing preferred phone, validate that the selected phone has a valid value
    if (name === 'preferred_phone') {
      const phoneValue = formData[`phone_${value.toLowerCase()}`];
      if (!isValidPhone(phoneValue)) {
        // Don't update if the phone number is invalid
        toast.error(`Cannot select ${value.toLowerCase()} phone as preferred: Invalid or missing number`);
        return;
      }
    }

    updateFieldValue(name, value);
  };

  const handleBillingAddressChange = (e) => {
    let data = {};
    if(e.target.checked) {
      data = {
        billing_address1: formData.address1,
        billing_address2: formData.address2,
        billing_city: formData.city,
        billing_state: formData.state,
        billing_postal_code: formData.postal_code,
      };
    } else {
      data = {
        billing_address1: user?.billing_address1 || '',
        billing_address2: user?.billing_address2 || '',
        billing_city: user?.billing_city || '',
        billing_state: user?.billing_state || '',
        billing_postal_code: user?.billing_postal_code || '',
      };
    }

    const nextIsBillingSameAddress = e.target.checked;
    setIsBillingSameAddress(nextIsBillingSameAddress);
    setFormData(prev => ({
       ...prev,
       ...data
     }));
    if (
      isBillingSameAddress !== nextIsBillingSameAddress
      || Object.keys(data).some(key => String(formData[key] ?? '') !== String(data[key] ?? ''))
    ) {
      onDirty?.();
    }
  }

  // --- Handle form submission ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    const effectiveSetError = isWorkflowStep ? parentSetError : setError;
    const effectiveSetLoading = isWorkflowStep ? parentSetLoading : setIsLoading;
    effectiveSetError('');

    const emailValid = isValidEmail(formData.email);
    const phoneValid = isValidPhone(formData.phone_mobile);

    if (!emailValid && !phoneValid) {
      effectiveSetError("Enter a valid email address or at least one valid phone number.");
      return;
    }
    if (String(formData.phone_mobile || '').trim() && !phoneValid) {
      effectiveSetError("Enter a valid US mobile phone number, or leave it blank.");
      return;
    }

    const allowedPreferredContact = availablePreferredContact(
      formData,
      formData.preferred_contact,
      isWorkflowStep ? registrationVerifiedContacts(initialData) : null,
    );
    if (!allowedPreferredContact || allowedPreferredContact !== formData.preferred_contact) {
      effectiveSetError("Choose a preferred contact method that is available.");
      return;
    }

    // A preferred phone is relevant only when at least one phone exists.
    if (phoneValid) {
      const preferredPhoneType = String(formData.preferred_phone || '').toLowerCase();
      const preferredPhoneValue = formData[`phone_${preferredPhoneType}`];
      if (!isValidPhone(preferredPhoneValue)) {
        effectiveSetError(`Your preferred phone (${preferredPhoneType || 'selection'}) is missing or invalid. Please enter a valid number or select a different preferred phone.`);
        return;
      }
    }

    effectiveSetLoading(true);
    setIsSubmitting(true);

    if (!userid) {
        effectiveSetError("Cannot update contact info: User ID is missing.");
        effectiveSetLoading(false); setIsSubmitting(false);
        return;
    }

    try {
      // Send formData directly as state keys match API keys
      const updateData = isBillingSameAddress
        ? applyBillingFromPrimaryAddress(formData)
        : { ...formData };
      // updateData.userid = userid; // If API needs it explicitly

      //const result = await api.updateUserInfo(updateData); // Send state directly
      const result = await userContext.updateUserInfo(updateData);
      if (result) {
        const hasVerification = Array.isArray(result.verification_sent) && result.verification_sent.length > 0;
        const successMessage = hasVerification && result.message
          ? result.message
          : "Contact info updated successfully.";
        if (isWorkflowStep) {
          // Pass back data using API names, potentially nested
          if (hasVerification) toast.success(successMessage);
          onSuccess({ registration_step: "contact", ...updateData });
        } else {
          toast.success(successMessage);
        }
        onSaveSuccess?.();
      } else {
        effectiveSetError(result.error || result.errorMsg || 'Contact info update failed.');
      }
    } catch {
      effectiveSetError('An error occurred during the update.');
    } finally {
      effectiveSetLoading(false);
      setIsSubmitting(false);
    }
  };

  // --- Render Logic ---
  if (isLoading || userContext?.loadingSession) {
      return (
          <div className="flex justify-center items-center h-64">
              <Spinner />
              <span className="ml-2 text-neutral-dark"></span>
          </div>
      );
  }
  // Check a required field like address1
  if (!isWorkflowStep && error && !formData.address1) {
     return <p className="text-center text-red-500 mt-4">{error}</p>;
  }

  return (
    // Outer container: Takes full height from parent
    <div className="h-full">
        {/* Form: flex column, takes full height, adds space between content and button */}
        <form onSubmit={handleSubmit} className="flex flex-col h-full space-y-4">
            {/* Content Wrapper: Scrolls if needed, ADD flex-grow */}
            <div className="flex-grow space-y-4  pb-4">
                {/* --- Address Section --- */}
                <fieldset className="border mt-4 p-4 rounded" disabled={isSubmitting}>
                    <legend className="text-md font-medium text-primary px-1 -mb-2">Address</legend>
                    <div className="mb-2">
                        <label htmlFor="address1" className={labelClassName}>Address *</label>
                        <input type="text" id="address1" name="address1" value={formData.address1} onChange={handleChange} className={inputClassName} required />
	                    </div>
	                    <div className="mb-2">
	                        <label htmlFor="address2" className="sr-only">Address line 2</label>
	                        <input type="text" id="address2" name="address2" value={formData.address2} onChange={handleChange} className={inputClassName} placeholder="Address Line 2 (Apt, Suite, etc.)" />
	                    </div>
                    <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                        <div className="flex-1 min-w-[150px]">
                            <label htmlFor="city" className={labelClassName}>City *</label>
                            <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} className={inputClassName} required />
                        </div>
                        <div className="flex-none w-28">
                            <label htmlFor="state" className={labelClassName}>State *</label>
                            <select id="state" name="state" value={formData.state} onChange={handleChange} className={`${inputClassName} bg-white`} required>
                                <option value="">Select</option>
                                {usStates.map(state => <option key={state} value={state}>{state}</option>)}
                            </select>
                        </div>
                        <div className="flex-none w-32">
                            <label htmlFor="postal_code" className={labelClassName}>Postal Code *</label>
                            <input type="text" id="postal_code" name="postal_code" value={formData.postal_code} onChange={handleChange} className={inputClassName} required />
                        </div>
                    </div>
                    <div className="flex items-center mt-3">
                      <input
                        type="checkbox"
                        id="isBillingSameAddress_chk"
                        name="isBillingSameAddress_chk"
                        onChange={handleBillingAddressChange}
                        checked={isBillingSameAddress}
                        className="h-6 w-6 flex-grow-0 flex-shrink-0 self-start border-2 rounded border-neutral-dark text-primary focus:ring-2 focus:ring-primary checked:bg-primary"
                        disabled={isSubmitting}
                      />
                      <label htmlFor="isBillingSameAddress_chk" className="ml-3 block text-sm text-neutral-dark">
                        Billing address is the same as above
                      </label>
                    </div>

                    {!isBillingSameAddress && (
                      <>
                        <div className="mb-2">
                            <label htmlFor="billing_address1" className={labelClassName}>Billing Address</label>
                            <input type="text" id="billing_address1" name="billing_address1" value={formData.billing_address1} onChange={handleChange} className={inputClassName}  />
	                        </div>
	                        <div className="mb-2">
	                            <label htmlFor="billing_address2" className="sr-only">Billing address line 2</label>
	                            <input type="text" id="billing_address2" name="billing_address2" value={formData.billing_address2} onChange={handleChange} className={inputClassName} placeholder="Address Line 2 (Apt, Suite, etc.)" />
	                        </div>
                        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                            <div className="flex-1 min-w-[150px]">
                                <label htmlFor="billing_city" className={labelClassName}>Billing City *</label>
                                <input type="text" id="billing_city" name="billing_city" value={formData.billing_city} onChange={handleChange} className={inputClassName} required />
                            </div>
                            <div className="flex-none w-28">
                                <label htmlFor="billing_state" className={labelClassName}>Billing State *</label>
                                <select id="billing_state" name="billing_state" value={formData.billing_state} onChange={handleChange} className={`${inputClassName} bg-white`} required>
                                    <option value="">Select</option>
                                    {usStates.map(state => <option key={state} value={state}>{state}</option>)}
                                </select>
                            </div>
                            <div className="flex-none w-32">
                                <label htmlFor="billing_postal_code" className={labelClassName}>Billing Postal Code *</label>
                                <input type="text" id="billing_postal_code" name="billing_postal_code" value={formData.billing_postal_code} onChange={handleChange} className={inputClassName} required />
                            </div>
                        </div>
                      </>
                    )}
                </fieldset>

                {/* --- Contact Section --- */}
                <fieldset className="border p-4 rounded" disabled={isSubmitting}>
                    <legend className="text-md font-medium text-primary px-1 -mb-2">Contact Information</legend>
                    <div className="mb-2">
                        <label htmlFor="email" className={labelClassName}>Email {isValidPhone(formData.phone_mobile) ? '' : '*'}</label>
                        <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={inputClassName} required={!isValidPhone(formData.phone_mobile)} />
                    </div>
                    <div className="flex flex-wrap items-end gap-x-3 gap-y-2 mb-2">
                        <div className="flex-1 min-w-[140px]">
                            <label htmlFor="phone_home" className={labelClassName}>
                              Home Phone
                              {formData.preferred_phone === 'HOME' && (
                                <span className="ml-2 text-xs text-primary font-medium">(Preferred)</span>
                              )}
                            </label>
                            <div className="relative">
	                              <IMaskInput
	                                mask={/^[+()\d\s.-]{0,25}$/}
	                                unmask={false}
                                id="phone_home"
                                name="phone_home"
                                value={formData.phone_home}
	                                onAccept={(value) => handleChange({ target: { name: 'phone_home', value } })}
	                                placeholder="Phone number"
	                                className={`${inputClassName} ${formData.preferred_phone === 'HOME' ? 'border-primary' : ''} ${!isValidPhone(formData.phone_home) && formData.phone_home ? 'pr-8' : ''}`}
	                                type="tel"
	                                aria-invalid={Boolean(!isValidPhone(formData.phone_home) && formData.phone_home)}
	                                aria-describedby={!isValidPhone(formData.phone_home) && formData.phone_home ? 'phone_home_error' : undefined}
	                              />
	                              {!isValidPhone(formData.phone_home) && formData.phone_home && (
	                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-red-500">
	                                  <span aria-hidden="true" title="Invalid phone number">!</span>
	                                  <span id="phone_home_error" className="sr-only">Home phone must be a valid US number.</span>
	                                </div>
	                              )}
                            </div>
                        </div>
                        <div className="flex-1 min-w-[140px]">
                            <label htmlFor="phone_work" className={labelClassName}>
                              Work Phone
                              {formData.preferred_phone === 'WORK' && (
                                <span className="ml-2 text-xs text-primary font-medium">(Preferred)</span>
                              )}
                            </label>
                            <div className="relative">
                              <IMaskInput
                                mask={/^[+()\d\s.-]{0,25}$/}
	                                unmask={false}
                                id="phone_work"
                                name="phone_work"
	                                value={formData.phone_work}
	                                onAccept={(value) => handleChange({ target: { name: 'phone_work', value } })}
	                                placeholder="Phone number"
	                                className={`${inputClassName} ${formData.preferred_phone === 'WORK' ? 'border-primary' : ''} ${!isValidPhone(formData.phone_work) && formData.phone_work ? 'pr-8' : ''}`}
	                                type="tel"
	                                aria-invalid={Boolean(!isValidPhone(formData.phone_work) && formData.phone_work)}
	                                aria-describedby={!isValidPhone(formData.phone_work) && formData.phone_work ? 'phone_work_error' : undefined}
	                              />
	                              {!isValidPhone(formData.phone_work) && formData.phone_work && (
	                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-red-500">
	                                  <span aria-hidden="true" title="Invalid phone number">!</span>
	                                  <span id="phone_work_error" className="sr-only">Work phone must be a valid US number.</span>
	                                </div>
	                              )}
                            </div>
                        </div>
                        <div className="flex-1 min-w-[140px]">
                            <label htmlFor="phone_mobile" className={labelClassName}>
                              Mobile Phone {!isValidEmail(formData.email) && '*'}
                              {formData.preferred_phone === 'MOBILE' && (
                                <span className="ml-2 text-xs text-primary font-medium">(Preferred)</span>
                              )}
                            </label>
                            <div className="relative">
	                              <IMaskInput
	                                mask={/^[+()\d\s.-]{0,25}$/}
	                                unmask={false}
                                id="phone_mobile"
                                name="phone_mobile"
	                                value={formData.phone_mobile}
	                                onAccept={(value) => handleChange({ target: { name: 'phone_mobile', value } })}
	                                placeholder="Phone number"
	                                className={`${inputClassName} ${formData.preferred_phone === 'MOBILE' ? 'border-primary' : ''} ${!isValidPhone(formData.phone_mobile) && formData.phone_mobile ? 'pr-8' : ''}`}
		                                required={!isValidEmail(formData.email)}
	                                type="tel"
	                                aria-invalid={Boolean(!isValidPhone(formData.phone_mobile) && formData.phone_mobile)}
	                                aria-describedby={!isValidPhone(formData.phone_mobile) && formData.phone_mobile ? 'phone_mobile_error' : undefined}
	                              />
	                              {!isValidPhone(formData.phone_mobile) && formData.phone_mobile && (
	                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-red-500">
	                                  <span aria-hidden="true" title="Invalid phone number">!</span>
	                                  <span id="phone_mobile_error" className="sr-only">Mobile phone must be a valid US number.</span>
	                                </div>
	                              )}
                            </div>
                        </div>
                    </div>
	                    <div className="flex flex-wrap items-center flex-row justify-around gap-x-6 gap-y-2 pt-2">
	                        {hasAnyValidPhone(formData) && <fieldset className="flex-shrink-0">
	                            <legend className="block text-sm font-medium text-gray-700 mb-1">Preferred Phone</legend>
	                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {['MOBILE', 'HOME', 'WORK'].map(phoneType => {
                                    // Check if this phone type has a valid number
                                    const phoneKey = `phone_${phoneType.toLowerCase()}`;
                                    const hasValidNumber = isValidPhone(formData[phoneKey]);
                                    const isDisabled = !hasValidNumber;

                                    return (
                                        <label key={phoneType} className={`inline-flex items-center ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                            <input
                                                type="radio"
                                                name="preferred_phone"
                                                value={phoneType}
                                                checked={formData.preferred_phone === phoneType}
                                                onChange={handlePreferredChange}
                                                className={radioClassName}
                                                disabled={isDisabled}
                                                title={isDisabled ? `${phoneType.toLowerCase()} phone number is missing or invalid` : ''}
                                            />
                                            <span className={`ml-2 capitalize text-sm ${isDisabled ? 'text-gray-400' : ''}`}>
                                                {phoneType.toLowerCase()}
                                                {isDisabled && <span className="ml-1 text-xs text-red-500">(invalid)</span>}
                                            </span>
                                        </label>
                                    );
	                                })}
	                            </div>
	                        </fieldset>}
	                        <fieldset className="flex-shrink-0">
	                            <legend className="block text-sm font-medium text-gray-700 mb-1">Preferred Contact *</legend>
	                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {['EMAIL', 'TEXT'].filter(contactType => {
                                    const availability = contactAvailability(
                                      formData,
                                      isWorkflowStep ? registrationVerifiedContacts(initialData) : null,
                                    );
                                    if (contactType === 'EMAIL') return availability.email;
                                    return availability.phone;
                                }).map(contactType => ( // Use API values
                                    <label key={contactType} className="inline-flex items-center">
                                        <input type="radio" name="preferred_contact" value={contactType} checked={formData.preferred_contact === contactType} onChange={handlePreferredChange} className={radioClassName} required />
                                        <span className="ml-2 text-sm">{contactType === 'TEXT' ? 'Text' : 'Email'}</span>
                                    </label>
	                                ))}
	                            </div>
	                        </fieldset>
	                    </div>
                </fieldset>
            </div> {/* End scrollable content wrapper */}

            {/* --- Submission Area --- */}
            <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-3 pb-8">
                {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
                { !isWorkflowStep && (<button
                    type="button"
                    onClick={onSaveComplete}
                    className={`w-full sm:w-1/4 bg-neutral-light text-neutral-dark p-3 rounded hover:bg-neutral-dark hover:text-white transition duration-200 font-semibold ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? '' : 'Close'}
                </button>
                )}
                <button
                    type="submit"
                    className={`${isWorkflowStep ? 'w-full':'w-full sm:w-1/4'} bg-button text-white p-3 rounded hover:bg-button-light transition duration-200 font-semibold ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Saving...' : (isWorkflowStep ? 'Save & Continue' : 'Save Contact Info')}
                </button>
            </div>
        </form>
    </div>
  );
}
export default ContactForm;
